import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MailIcon, CheckIcon, XIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getPendingInvites, respondToInvite } from '@/lib/api';
import type { SnapInvite } from '@/types/database.types';
import Button from './form/Button';

export default function PendingInvites() {
  const [invites, setInvites] = useState<SnapInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchInvites = async () => {
    try {
      setLoading(true);
      const response = await getPendingInvites();
      setInvites(response.data);
    } catch (error) {
      console.error('Error fetching invites:', error);
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const handleResponse = async (inviteId: string, accept: boolean) => {
    try {
      await respondToInvite(inviteId, accept);
      toast.success(accept ? 'Invitation accepted' : 'Invitation declined');
      setInvites(prev => prev.filter(invite => invite.id !== inviteId));
      
      // If accepted, navigate to the SNAP
      if (accept) {
        const invite = invites.find(i => i.id === inviteId);
        if (invite?.snap?.id) {
          navigate(`/snap/${invite.snap.id}`);
        }
      }
    } catch (error) {
      console.error('Error responding to invite:', error);
      toast.error('Failed to respond to invitation');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (invites.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
      <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700">
        <h2 className="text-lg font-semibold text-white flex items-center">
          <MailIcon className="h-5 w-5 mr-2" />
          Pending Invitations
        </h2>
      </div>
      
      <div className="divide-y divide-gray-200">
        {invites.map((invite) => (
          <div key={invite.id} className="p-4 hover:bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {invite.snap?.title}
                </h3>
                <div className="mt-1 text-sm text-gray-600">
                  Invited by <span className="font-medium text-gray-900">{invite.inviter_username}</span>
                </div>
                {invite.message && (
                  <p className="mt-2 text-sm text-gray-600 italic">
                    "{invite.message}"
                  </p>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleResponse(invite.id, false)}
                  icon={<XIcon className="h-4 w-4" />}
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                >
                  Decline
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleResponse(invite.id, true)}
                  icon={<CheckIcon className="h-4 w-4" />}
                >
                  Accept
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}